import { ReactNode } from 'react';

import {
  BookOpenIcon,
  CheckMarkIcon,
  FaceWinkIcon,
  FolderIcon,
  GlobeIcon,
  SettingsIcon,
  SlidersIcon,
  TranslateIcon,
  UsersIcon,
  VoiceIcon,
} from '@braskai/icons';

import { ZipZapIcon } from '@/shared/assets/icons/ZipZapIcon';

type TrialFeature = {
  name: string;
  description: string;
  icon: ReactNode;
};

export type TrialPlan = {
  name: string;
  title: string;
  previousFeatures?: {
    name: string;
    features: TrialFeature[];
  };
  chipLabel: string;
  features: TrialFeature[];
  minutes: number;
  price: string;
};

// Helper function to create icons with consistent sizing
const createIcon = (IconComponent: any, additionalProps = {}) => (
  <IconComponent height={20} width={20} {...additionalProps} />
);

// Shared features dictionary to eliminate duplication
const SHARED_FEATURES = {
  api: {
    name: 'Automate and scale with our API',
    description: 'integrate translation into your workflow or app',
    icon: createIcon(GlobeIcon),
  },
  customizeVoice: {
    name: 'Customize voice cloning',
    description: 'tune cloned voices and create your own library',
    icon: createIcon(SettingsIcon),
  },
  editDetail: {
    name: 'Edit every detail',
    description: 'built-in editor with unlimited free redubs',
    icon: createIcon(SlidersIcon),
  },
  lipSync: {
    name: 'Lip-sync your translated videos',
    description: 'single speakers and multi-person conversations',
    icon: createIcon(FaceWinkIcon),
  },
  translate: {
    name: 'Translate to 135 languages',
    description: 'with AI transcription, translation, and voiceover ',
    icon: createIcon(TranslateIcon),
  },
  voiceClone: {
    name: 'Clone your voice in 32 languages',
    description: 'to keep your authentic sound in all translations ',
    icon: createIcon(VoiceIcon),
  },
} as const;

// Helper function to create "Start from X minutes" feature
const createMinutesFeature = (minutes: number): TrialFeature => ({
  name: `Start from ${minutes} minutes`,
  description: 'for translations and lip-sync, get more anytime',
  icon: createIcon(CheckMarkIcon),
});

export const PLANS: TrialPlan[] = [
  {
    name: 'Creator',
    title: 'Unlock all download options',
    chipLabel: 'Great for start',
    features: [
      createMinutesFeature(25),
      SHARED_FEATURES.translate,
      SHARED_FEATURES.voiceClone,
      SHARED_FEATURES.editDetail,
      SHARED_FEATURES.api,
    ],
    minutes: 25,
    price: '$2.4',
  },
  {
    name: 'Creator Pro',
    title: 'Unlock unlimited edits',
    chipLabel: 'Most popular',
    features: [
      createMinutesFeature(100),
      SHARED_FEATURES.lipSync,
      SHARED_FEATURES.customizeVoice,
      {
        name: 'Work together with your team',
        description: 'share your subscription with 2 more members',
        icon: createIcon(UsersIcon),
      },
    ],
    minutes: 100,
    previousFeatures: {
      name: 'and all Creator features',
      features: [
        SHARED_FEATURES.translate,
        SHARED_FEATURES.voiceClone,
        SHARED_FEATURES.editDetail,
        SHARED_FEATURES.api,
      ],
    },
    price: '$1.5',
  },
  {
    name: 'Business',
    title: 'Unlock team potential',
    chipLabel: 'Most precise result',
    features: [
      createMinutesFeature(500),
      {
        name: 'Keep translations consistent',
        description: 'create custom dictionary for your terminology',
        icon: createIcon(BookOpenIcon),
      },
      {
        name: 'Expand your voice clone library',
        description: 'build collection of voice clones for your brand',
        icon: createIcon(FolderIcon),
      },
      {
        name: 'Bring your whole team',
        description: 'get more videos done with 4 team members',
        icon: createIcon(UsersIcon),
      },
      {
        name: 'Work faster',
        description: 'enjoy priority processing and shorter wait times',
        icon: createIcon(ZipZapIcon),
      },
    ],
    minutes: 500,
    previousFeatures: {
      name: 'and all Creator and Creator Pro features',
      features: [
        SHARED_FEATURES.translate,
        SHARED_FEATURES.voiceClone,
        SHARED_FEATURES.editDetail,
        SHARED_FEATURES.api,
        SHARED_FEATURES.lipSync,
        SHARED_FEATURES.customizeVoice,
      ],
    },
    price: '$1.5',
  },
];
